import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { movieBookingApi } from '../../services/movieBookingApi';
import { connectSeatWebSocket } from '../../services/seatWebSocket';
import { useAuth } from '../../context/AuthContext';
import './MovieBooking.css';

const SeatSelection = () => {
  const { showId: routeShowId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showId, setShowId] = useState(routeShowId);
  const [seatMap, setSeatMap] = useState({});
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsError, setWsError] = useState(null);

  useEffect(() => {
    let socket;
    const loadSeatMap = async () => {
      try {
        const response = await movieBookingApi.getSeatMap(showId);
        setSeatMap(response.data?.seats || {});
      } catch (error) {
        setSeatMap({});
      } finally {
        setLoading(false);
      }
    };

    if (showId) {
      loadSeatMap();
      socket = connectSeatWebSocket(showId, (event) => {
        if (event.type === 'SEAT_SNAPSHOT' && event.seats) {
          setSeatMap(event.seats);
        }
        if (event.type === 'SEAT_STATUS' && event.seatId) {
          setSeatMap((prev) => ({
            ...prev,
            [event.seatId]: {
              ...(prev[event.seatId] || {}),
              status: event.status,
              selectedBy: event.selectedBy,
              selectionExpiry: event.expiresAt,
            },
          }));
        }
      }, setWsError);
    }

    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, [showId]);

  const seats = useMemo(() => Object.entries(seatMap), [seatMap]);

  const toggleSeat = (seatId, seatState) => {
    if (!seatState || seatState.status === 'BOOKED' || seatState.status === 'UNDER_PROCESS') {
      return;
    }
    setSelectedSeats((prev) => (
      prev.includes(seatId) ? prev.filter((id) => id !== seatId) : [...prev, seatId]
    ));
  };

  const handleBooking = async () => {
    if (!selectedSeats.length) {
      return;
    }
    setBookingStatus('PROCESSING');
    try {
      const pricingResponse = await movieBookingApi.calculatePricing({
        showId,
        seatIds: selectedSeats,
        basePrice: 200,
        afternoonShow: false,
      });
      setPricing(pricingResponse.data);

      const idempotencyKey = crypto.randomUUID();
      const bookingResponse = await movieBookingApi.createBooking({
        showId,
        userId: user?.username || 'guest',
        seatIds: selectedSeats,
        basePrice: 200,
        afternoonShow: false,
      }, idempotencyKey);

      const bookingId = bookingResponse.data?.bookingId;
      await movieBookingApi.lockSeats(showId, {
        seatIds: selectedSeats,
        bookingId,
        lockMinutes: 5,
      });

      await movieBookingApi.authorizePayment({
        bookingId,
        amount: bookingResponse.data?.finalTotal || pricingResponse.data?.finalTotal || 0,
        showId,
        seatIds: selectedSeats,
      });

      await movieBookingApi.capturePayment({ bookingId });
      navigate(`/bookings/${bookingId}`);
    } catch (error) {
      setBookingStatus('FAILED');
    }
  };

  const requiresShowIdInput = showId?.includes(':');

  return (
    <div className="movie-booking-page">
      <div className="movie-booking-header">
        <div>
          <h1 className="movie-booking-title">Select Seats</h1>
          <p className="movie-booking-note">Show ID: {showId}</p>
          {wsError && <p className="movie-booking-note">WebSocket: {wsError}</p>}
        </div>
        <button className="movie-booking-button secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {requiresShowIdInput && (
        <div className="movie-booking-card">
          <p className="movie-booking-note">
            Showtime values are used as IDs in this route. If your showId is different, paste it below.
          </p>
          <div className="movie-booking-form">
            <input
              className="movie-booking-input"
              placeholder="Paste showId"
              value={showId}
              onChange={(event) => setShowId(event.target.value)}
            />
          </div>
        </div>
      )}

      {loading && <p>Loading seat map...</p>}
      {!loading && (
        <>
          <div className="movie-booking-seat-grid">
            {seats.map(([seatId, seatState]) => {
              const isSelected = selectedSeats.includes(seatId);
              const isUnavailable = seatState?.status === 'BOOKED' || seatState?.status === 'UNDER_PROCESS';
              return (
                <button
                  key={seatId}
                  className={`movie-booking-seat ${isSelected ? 'selected' : ''} ${isUnavailable ? 'unavailable' : ''}`}
                  onClick={() => toggleSeat(seatId, seatState)}
                  type="button"
                >
                  {seatId}
                </button>
              );
            })}
          </div>

          <div className="movie-booking-card">
            <h3>Selected seats: {selectedSeats.join(', ') || 'None'}</h3>
            {pricing && (
              <p className="movie-booking-note">
                Total: {pricing.finalTotal} (Discounts: {pricing.discountTotal})
              </p>
            )}
            <div className="movie-booking-actions">
              <button className="movie-booking-button" onClick={handleBooking}>
                Confirm & Pay
              </button>
              {bookingStatus === 'FAILED' && (
                <span className="movie-booking-note">Booking failed. Try again.</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SeatSelection;
